import React, { useEffect, useRef, useState } from 'react';
import { MagnifyingGlass, MapPin } from '@phosphor-icons/react';
import { LatLng } from 'utils/spotSearch';
import { PlaceSuggestion, ResolvedPlace, newSessionToken, retrievePlace, suggestPlaces } from 'utils/geocoding';
import styles from './FindParking.module.css';

type FindParkingSearchProps = {
    /** Biases suggestions toward the current map center. */
    proximity?: LatLng;
    onSelect: (place: ResolvedPlace) => void;
};

const DEBOUNCE_MS = 250;
const LISTBOX_ID = 'find-parking-suggestions';

const FindParkingSearch: React.FC<FindParkingSearchProps> = ({ proximity, onSelect }) => {
    const [query, setQuery] = useState('');
    const [suggestions, setSuggestions] = useState<PlaceSuggestion[]>([]);
    // The query the current suggestions were fetched for; stale lists must not be submitted.
    const [suggestionsQuery, setSuggestionsQuery] = useState('');
    const [open, setOpen] = useState(false);
    const [highlighted, setHighlighted] = useState(-1);
    const [searching, setSearching] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const formRef = useRef<HTMLFormElement>(null);
    // Read through a ref so map moves don't re-trigger the suggestion fetch.
    const proximityRef = useRef(proximity);
    proximityRef.current = proximity;
    // One Mapbox search session per typing-and-picking cycle; renewed after each pick.
    const sessionRef = useRef(newSessionToken());
    // Set when `choose` writes the picked name into the input, so that change doesn't re-run suggest.
    const skipNextSuggestRef = useRef(false);
    // Bumped on every pick so a suggest fetch still in flight cannot reopen the list afterwards.
    const suggestGenerationRef = useRef(0);

    useEffect(() => {
        if (skipNextSuggestRef.current) {
            skipNextSuggestRef.current = false;
            return;
        }
        if (!query.trim()) {
            setSuggestions([]);
            setSuggestionsQuery('');
            setOpen(false);
            return;
        }
        const controller = new AbortController();
        const generation = suggestGenerationRef.current;
        const timer = window.setTimeout(async () => {
            try {
                const results = await suggestPlaces(query, {
                    sessionToken: sessionRef.current,
                    proximity: proximityRef.current,
                    signal: controller.signal,
                });
                if (controller.signal.aborted || generation !== suggestGenerationRef.current) return;
                setSuggestions(results);
                setSuggestionsQuery(query);
                setHighlighted(-1);
                setOpen(true);
            } catch (e) {
                // Suggestions are best-effort; the submit path reports errors.
            }
        }, DEBOUNCE_MS);
        return () => {
            window.clearTimeout(timer);
            controller.abort();
        };
    }, [query]);

    useEffect(() => {
        const handleOutsideClick = (event: MouseEvent) => {
            if (formRef.current && !formRef.current.contains(event.target as Node)) setOpen(false);
        };
        document.addEventListener('mousedown', handleOutsideClick);
        return () => document.removeEventListener('mousedown', handleOutsideClick);
    }, []);

    const choose = async (suggestion: PlaceSuggestion) => {
        suggestGenerationRef.current += 1;
        skipNextSuggestRef.current = suggestion.name !== query;
        setQuery(suggestion.name);
        setSuggestions([]);
        setSuggestionsQuery('');
        setOpen(false);
        setError(null);
        setSearching(true);
        try {
            const place = await retrievePlace(suggestion, sessionRef.current);
            sessionRef.current = newSessionToken();
            onSelect(place);
        } catch (e) {
            setError('That place could not be located. Try another result.');
        } finally {
            setSearching(false);
        }
    };

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        // Only trust the list when it was fetched for exactly what is in the box right now.
        const current = suggestionsQuery === query ? suggestions : [];
        if (highlighted >= 0 && current[highlighted]) {
            await choose(current[highlighted]);
            return;
        }
        if (current[0]) {
            await choose(current[0]);
            return;
        }
        if (!query.trim()) {
            setError('Enter an address, city, or place to search.');
            return;
        }
        setSearching(true);
        setError(null);
        try {
            const results = await suggestPlaces(query, {
                sessionToken: sessionRef.current,
                proximity: proximityRef.current,
                limit: 1,
            });
            if (results[0]) {
                await choose(results[0]);
            } else {
                setError('No places match that search. Try adding a city or state.');
            }
        } catch (e) {
            setError('Search is unavailable right now. Try again in a moment.');
        } finally {
            setSearching(false);
        }
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (!open || suggestions.length === 0) return;
        if (event.key === 'ArrowDown') {
            event.preventDefault();
            setHighlighted((current) => (current + 1) % suggestions.length);
        } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            setHighlighted((current) => (current <= 0 ? suggestions.length - 1 : current - 1));
        } else if (event.key === 'Escape') {
            setOpen(false);
        }
    };

    const showSuggestions = open && suggestions.length > 0;

    return (
        <header className={styles.searchHeader}>
            <h1 className={styles.title}>Find Parking</h1>
            <form ref={formRef} className={styles.searchForm} role="search" onSubmit={handleSubmit}>
                <div className={styles.searchField}>
                    <MagnifyingGlass size={20} className={styles.searchIcon} aria-hidden="true" />
                    <input
                        className={styles.searchInput}
                        type="text"
                        value={query}
                        placeholder="Enter an Address, City, or Place"
                        aria-label="Search for a location"
                        autoComplete="off"
                        role="combobox"
                        aria-autocomplete="list"
                        aria-expanded={showSuggestions}
                        aria-controls={LISTBOX_ID}
                        aria-activedescendant={highlighted >= 0 ? `${LISTBOX_ID}-${highlighted}` : undefined}
                        onChange={(event) => {
                            setQuery(event.target.value);
                            setError(null);
                        }}
                        onFocus={() => {
                            if (suggestions.length > 0) setOpen(true);
                        }}
                        onKeyDown={handleKeyDown}
                    />
                    <button type="submit" className={styles.searchButton} disabled={searching}>
                        {searching ? 'Searching' : 'Search'}
                    </button>

                    {showSuggestions && (
                        <ul id={LISTBOX_ID} role="listbox" className={styles.suggestions}>
                            {suggestions.map((place, index) => (
                                <li
                                    key={place.id}
                                    id={`${LISTBOX_ID}-${index}`}
                                    role="option"
                                    aria-selected={index === highlighted}
                                    className={`${styles.suggestion} ${
                                        index === highlighted ? styles.suggestionActive : ''
                                    }`}
                                    onMouseDown={(event) => event.preventDefault()}
                                    onMouseEnter={() => setHighlighted(index)}
                                    onClick={() => choose(place)}
                                >
                                    <MapPin size={16} className={styles.suggestionIcon} aria-hidden="true" />
                                    <span className={styles.suggestionText}>
                                        <span className={styles.suggestionName}>{place.name}</span>
                                        {place.detail && (
                                            <span className={styles.suggestionDetail}>{place.detail}</span>
                                        )}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
                {error && (
                    <p className={styles.searchError} role="alert">
                        {error}
                    </p>
                )}
            </form>
        </header>
    );
};

export default FindParkingSearch;
